import React, { useState, useEffect } from "react";
import { auth, dataConnect } from "../firebaseConfig";
import {
    signInWithPopup,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signInWithCredential,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    OAuthProvider
} from "firebase/auth";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { upsertUser } from "@firebasegen/default-connector";
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Divider,
    Alert,
    InputAdornment,
    CircularProgress,
    Avatar,
    Drawer,
    IconButton
} from "@mui/material";
import { Google as GoogleIcon, Apple as AppleIcon, Email as EmailIcon, Close as CloseIcon } from "@mui/icons-material";

const LoginPage = () => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isEmailLinkSent, setIsEmailLinkSent] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Where to go after signing in (only paths inside the app), e.g. the MCP approval page
    const next = searchParams.get("next");
    const afterLogin = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                navigate(afterLogin);
            }
        });
        return () => unsubscribe();
    }, [navigate, afterLogin]);

    useEffect(() => {
        // Handle deep links for Capacitor
        const handleDeepLink = (url) => {
            if (isSignInWithEmailLink(auth, url)) {
                let storedEmail = window.localStorage.getItem("emailForSignIn");
                if (!storedEmail) {
                    storedEmail = prompt("Please enter your email to confirm sign-in");
                }
                if (storedEmail) {
                    setLoading(true);
                    signInWithEmailLink(auth, storedEmail, url)
                        .then((result) => {
                            window.localStorage.removeItem("emailForSignIn");
                            navigate(afterLogin);
                        })
                        .catch((err) => {
                            console.error("Deep link sign-in error:", err);
                            setError(err.message);
                            setLoading(false);
                            setDrawerOpen(true); // Open drawer to show error
                        });
                }
            }
        };

        // Check for Web/PWA logic upon load
        if (isSignInWithEmailLink(auth, window.location.href)) {
            handleDeepLink(window.location.href);
        }

        // Listen for native app deep links
        if (Capacitor.isNativePlatform()) {
            import('@capacitor/app').then(({ App }) => {
                App.addListener('appUrlOpen', (data) => {
                    handleDeepLink(data.url);
                });
            });
        }
    }, [navigate, afterLogin]);

    const handleEmailLinkLogin = async (e) => {
        e.preventDefault();
        if (!email) {
            setError("Enter your email to receive the login link.");
            return;
        }

        setError(null);
        setLoading(true);

        // Special handling for the demo account to bypass magic link for Apple testers
        if (email.toLowerCase() === 'demo@tourminator.no') {
            try {
                await signInWithEmailAndPassword(auth, email, "testtest");
                navigate("/");
                return;
            } catch (err) {
                console.error("Demo login failed:", err);
                setError("Demo login failed. Please try again or use another method.");
                setLoading(false);
                return;
            }
        }

        const actionCodeSettings = {
            // For native apps, use the hosting URL to support Universal Links
            // fallback to window.location.href if on web
            url: Capacitor.isNativePlatform()
                ? 'https://tourminator.no/login'
                : window.location.href,
            handleCodeInApp: true,
            iOS: {
                bundleId: 'no.tourminator.app'
            }
        };

        try {
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            window.localStorage.setItem("emailForSignIn", email);
            setIsEmailLinkSent(true);
            setLoading(false);
        } catch (err) {
            if (err.code === "auth/user-not-found" || err.message.includes("not found")) {
                try {
                    await upsertUser(dataConnect, { email });
                    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
                    window.localStorage.setItem("emailForSignIn", email);
                    setIsEmailLinkSent(true);
                } catch (retryErr) {
                    setError(retryErr.message);
                }
            } else {
                setError(err.message);
            }
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError(null);
        setLoading(true);
        try {
            let userCredential;
            const provider = new GoogleAuthProvider();


            if (Capacitor.getPlatform() === 'web') {
                userCredential = await signInWithPopup(auth, provider);

                // Extract access token for Google Drive API calls
                const credential = GoogleAuthProvider.credentialFromResult(userCredential);
                if (credential?.accessToken) {
                    localStorage.setItem('googleAccessToken', credential.accessToken);
                    console.log("Successfully captured Google Access Token for Drive.");
                }
            } else {
                await SocialLogin.initialize({
                    google: {
                        iOSClientId: "409655446693-tmd2kjt38jrst965vbf5ohprkdgtpe5u.apps.googleusercontent.com"
                    }
                });
                const result = await SocialLogin.login({
                    provider: 'google',
                    options: {
                        scopes: ['email', 'profile']
                    }
                });
                if (!result?.result?.idToken) throw new Error('No valid credential received');

                const credential = GoogleAuthProvider.credential(result.result.idToken);
                userCredential = await signInWithCredential(auth, credential);

                if (result.result.accessToken) {
                    localStorage.setItem('googleAccessToken', result.result.accessToken);
                }
            }

            // Only send email/photo on login upsert — never overwrite name.
            // Name is handled by OnboardingDialog for new users.
            await upsertUser(dataConnect, {
                email: userCredential.user.email,
                photo: userCredential.user.photoURL,
            });
            navigate("/");
        } catch (err) {
            console.error(err);
            setError("Google sign-in failed. Please try again.");
            setLoading(false);
        }
    };

    const handleAppleLogin = async () => {
        setError(null);
        setLoading(true);
        try {
            let userCredential;
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');

            if (Capacitor.getPlatform() === 'web') {
                userCredential = await signInWithPopup(auth, provider);
            } else {
                // Generate a random raw nonce
                const rawNonce = Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12);

                // Hash the nonce using SHA-256 for Apple
                const msgBuffer = new TextEncoder().encode(rawNonce);
                const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashedNonce = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

                console.log("DEBUG - Starting SocialLogin.login with hashed nonce...");
                const result = await SocialLogin.login({
                    provider: 'apple',
                    options: {
                        scopes: ['email', 'name'],
                        nonce: hashedNonce // Pass the HASHED nonce to Apple
                    }
                });

                if (!result?.result?.idToken) throw new Error('No valid credential received');

                console.log("DEBUG - Apple Result received. Firebase verifying...");
                console.log("DEBUG - idToken length:", result.result.idToken?.length);
                console.log("DEBUG - rawNonce length:", rawNonce.length);

                const credential = provider.credential({
                    idToken: result.result.idToken,
                    rawNonce: rawNonce // Pass the RAW nonce to Firebase
                });

                console.log("DEBUG - Attempting Firebase sign-in with Apple credential...");

                // Add a manual timeout to the Firebase call to avoid indefinite hangs
                const signInPromise = signInWithCredential(auth, credential);
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("Firebase signInWithCredential timed out after 15s")), 15000)
                );

                userCredential = await Promise.race([signInPromise, timeoutPromise]);
                console.log("DEBUG - Firebase sign-in successful! User UID:", userCredential.user.uid);
            }

            // Upsert user logic
            console.log("DEBUG - Starting user upsert to Data Connect...");
            try {
                // Only send email/photo on login upsert — never overwrite name.
                // Apple only provides displayName on the FIRST sign-in; subsequent logins
                // return null, which would overwrite the user's real name with fallback values.
                // Name is handled by OnboardingDialog for new users.
                await upsertUser(dataConnect, {
                    email: userCredential.user.email,
                    photo: userCredential.user.photoURL,
                });
                console.log("DEBUG - User upsert successful.");
            } catch (upsertErr) {
                console.error("DEBUG - Data Connect Upsert Error (non-fatal):", upsertErr);
                // We keep going if upsert fails but login succeeded
            }

            console.log("DEBUG - Navigating to home...");
            navigate("/");
        } catch (err) {
            console.error("DEBUG - Apple Login Error Full:", JSON.stringify(err, Object.getOwnPropertyNames(err)));

            // Extract a readable message
            let displayError = "Apple sign-in failed. Please try again.";
            if (err.message && err.message.includes('invalid-nonce')) {
                displayError = "Apple nonce mismatch. Please try again or contact support.";
            } else if (err.code) {
                displayError = `Auth Error (${err.code}): ${err.message}`;
            }

            setError(displayError);
            setLoading(false);
        }
    };

    const toggleDrawer = (newOpen) => () => {
        setDrawerOpen(newOpen);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                height: '100dvh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                position: 'relative',
                overflow: 'hidden',
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)'
            }}
        >
            {/* Background Accents (Orbs) */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -100,
                    left: -100,
                    width: 300,
                    height: 300,
                    bgcolor: '#ff6b00',
                    borderRadius: '50%',
                    filter: 'blur(100px)',
                    opacity: 0.15,
                    zIndex: 0
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '20%',
                    right: -50,
                    width: 250,
                    height: 250,
                    bgcolor: '#ff9e00',
                    borderRadius: '50%',
                    filter: 'blur(80px)',
                    opacity: 0.15,
                    zIndex: 0
                }}
            />

            {/* Main Content - Branding */}
            <Box sx={{ p: 4, zIndex: 1, mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                    src="/tourminator_icon.jpg"
                    sx={{ width: 96, height: 96, mb: 3, border: '3px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
                />
                <Typography component="h1" variant="h3" fontWeight="800" color="white" align="center" sx={{ letterSpacing: '-0.02em', mb: 1.5, fontSize: { xs: '2.5rem', sm: '3rem' } }}>
                    Tourminator
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, maxWidth: 300 }}>
                    The ultimate tool for tour management.
                </Typography>
            </Box>

            {/* Bottom Section - CTA */}
            <Box sx={{ p: 3, pb: 6, zIndex: 1, backdropFilter: 'blur(10px)', bgcolor: 'rgba(0,0,0,0.2)' }}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={toggleDrawer(true)}
                    sx={{
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        borderRadius: 3,
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        boxShadow: '0 8px 24px rgba(33, 150, 243, 0.4)',
                        textTransform: 'none',
                        color: 'white',
                    }}
                >
                    Log in or create account
                </Button>
                <Typography variant="caption" display="block" align="center" sx={{ mt: 2, color: 'rgba(255,255,255,0.4)' }}>
                    By continuing, you accept our Terms of Service.
                </Typography>
            </Box>

            {/* Login Drawer */}
            <Drawer
                anchor="bottom"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 24,
                        borderTopRightRadius: 24,
                        bgcolor: 'rgba(15, 23, 42, 0.98)',
                        color: 'white',
                        maxHeight: '85vh'
                    }
                }}
            >
                <Box sx={{ p: 3, pt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                    {/* Handle bar */}
                    <Box sx={{ width: 40, height: 4, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, mb: 3, mt: 1 }} />

                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h5" fontWeight="700" color="white">
                            Get Started
                        </Typography>
                        <IconButton onClick={toggleDrawer(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Social Login Buttons */}
                    <Box sx={{ width: '100%', mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            startIcon={<GoogleIcon />}
                            sx={{
                                py: 1.5,
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.15)',
                                bgcolor: 'rgba(255,255,255,0.02)',
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.08)'
                                },
                                textTransform: 'none',
                                borderRadius: 3,
                                fontSize: '1rem',
                                fontWeight: 500
                            }}
                        >
                            Continue with Google
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleAppleLogin}
                            disabled={loading}
                            startIcon={<AppleIcon />}
                            sx={{
                                py: 1.5,
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.15)',
                                bgcolor: 'rgba(255,255,255,0.02)',
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.08)'
                                },
                                textTransform: 'none',
                                borderRadius: 3,
                                fontSize: '1rem',
                                fontWeight: 500
                            }}
                        >
                            Continue with Apple
                        </Button>
                    </Box>

                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(255,255,255,0.2)',
                        mb: 3
                    }}>
                        <Divider sx={{ flex: 1, borderColor: 'currentColor' }} />
                        <Typography variant="caption" sx={{ px: 2, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                            OR
                        </Typography>
                        <Divider sx={{ flex: 1, borderColor: 'currentColor' }} />
                    </Box>

                    {/* Magic Link Login Form */}
                    <Box component="form" onSubmit={handleEmailLinkLogin} sx={{ width: '100%' }}>
                        <TextField
                            margin="none"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: 'white',
                                    fontSize: '16px',
                                    borderRadius: 3
                                }
                            }}
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused fieldset': { borderColor: '#21CBF3' },
                                }
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 3,
                                background: 'linear-gradient(45deg, #ff6b00 30%, #ff9e00 90%)',
                                boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)',
                                textTransform: 'none',
                                '&:disabled': {
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'rgba(255,255,255,0.3)'
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Continue with Email"}
                        </Button>

                        {isEmailLinkSent && (
                            <Alert severity="success" sx={{ mt: 2, mb: 1, borderRadius: 2 }}>
                                Magic Link sent! Check your email.
                            </Alert>
                        )}
                    </Box>
                    <Box sx={{ minHeight: 20 }} />
                </Box>
            </Drawer>
        </Box>
    );
};

export default LoginPage;